/**
 * The server-side-pose kill switch, read from one Firestore document.
 *
 * ── Why runtime config and not a build-time flag ─────────────────────────
 *
 * A build-time flag means rebuilding and redeploying **eight** Firebase Hosting
 * targets to turn a feature off, and users stay on the cached bundle until
 * `ensureFreshSW`'s hourly check fires anyway. One Firestore field is one edit,
 * effective on the next page load, across all eight at once — which also makes
 * the staged rollout (plan §8.1) a config change rather than eight deploys.
 *
 * ── Everything below is about failing safe ───────────────────────────────
 *
 * The dangerous direction is unambiguous. On-device is the measured-better path
 * whenever it works — a `standard` iPhone runs 12.8 Hz on-device against 0.6 Hz
 * through the server, with 94% of the attempt seeing no pose at all (plan
 * §2.4a). So "we could not read the config", "the document is malformed" and
 * "this field is the wrong type" all have the same answer: device only.
 *
 * The type checks are not pedantry. `serverPoseEnabled: "false"` is a truthy
 * string, and someone typing a value into a console field instead of toggling it
 * is exactly how a kill switch gets silently armed.
 */

import type { DeviceClass } from "./alignmentTuning";

export interface PoseRemoteConfig {
  enabled: boolean;
  deviceClasses: DeviceClass[];
  onModelFailure: boolean;
  /** Spec 2026-09-11 §4.5. Strict booleans, all off unless the document says so. */
  onBenchmarkFail: boolean;
  onDetectorBroken: boolean;
  onBlindWhileVisible: boolean;
  /** Switch models once when the running one cannot see a gated joint (liveHealth.ts). */
  onJointUnseen: boolean;
  rememberLiveSwitch: boolean;
  /** Median round trip above which the client falls back to the device. */
  maxRttMs: number;
  // No send-rate field, deliberately. The alignment loop awaits each frame
  // before it takes the next, so its rate is the round trip's; a field nothing
  // reads would be a knob that parses cleanly and does nothing.
}

/**
 * What every failure path returns, and what the app runs on until a valid
 * document says otherwise. Identical in effect to the feature not existing.
 */
export const DEFAULT_POSE_REMOTE_CONFIG: PoseRemoteConfig = {
  enabled: false,
  deviceClasses: [],
  onModelFailure: false,
  onBenchmarkFail: false,
  onDetectorBroken: false,
  onBlindWhileVisible: false,
  onJointUnseen: false,
  rememberLiveSwitch: false,
  maxRttMs: 400,
};

/**
 * The ONLY classes a document may route, whatever it says.
 *
 * `standard` and `unknown` are deliberately absent and cannot be added by
 * config. `standard` is the 81% for whom nothing is wrong — routing a healthy
 * handset destroys a scan that works today. `unknown` means unmeasured, and the
 * existing fail-safe maps it to the standard path; BUG-0004's population is
 * reached through the models-failed trigger, never through their class. Making
 * this a floor in code rather than a convention in the document means a bad edit
 * cannot reach the population with the most to lose.
 */
const ROUTABLE_CLASSES: DeviceClass[] = ["assisted", "marginal"];

const RTT_MIN_MS = 100;
const RTT_MAX_MS = 2000;

function readBool(value: unknown): boolean {
  // Strict identity, never truthiness — see the header.
  return value === true;
}

function readClamped(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || !isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

function readClasses(value: unknown): DeviceClass[] {
  if (!Array.isArray(value)) return [];
  const out: DeviceClass[] = [];
  for (let i = 0; i < value.length; i++) {
    const entry = value[i];
    if (typeof entry !== "string") continue;
    if (ROUTABLE_CLASSES.indexOf(entry as DeviceClass) === -1) continue;
    if (out.indexOf(entry as DeviceClass) !== -1) continue;
    out.push(entry as DeviceClass);
  }
  return out;
}

export interface PoseRemoteConfigDeps {
  /** Resolves the raw document data, or null/undefined when it does not exist. */
  readDoc: () => Promise<unknown>;
}

/**
 * Read once at boot. Never throws.
 *
 * A one-shot Firestore `getDoc` has no cache and no retry and fails
 * intermittently (`HERE_BE_DRAGONS.md` § "Firestore reads"), so the caller must
 * be able to treat this as non-blocking — which it can, because every failure
 * resolves the device-only default rather than rejecting.
 */
export async function loadPoseRemoteConfig(
  deps: PoseRemoteConfigDeps
): Promise<PoseRemoteConfig> {
  let doc: unknown;
  try {
    doc = await deps.readDoc();
  } catch (e) {
    return DEFAULT_POSE_REMOTE_CONFIG;
  }

  if (!doc || typeof doc !== "object") return DEFAULT_POSE_REMOTE_CONFIG;
  const d = doc as Record<string, unknown>;

  return {
    enabled: readBool(d.serverPoseEnabled),
    deviceClasses: readClasses(d.serverPoseDeviceClasses),
    onModelFailure: readBool(d.serverPoseOnModelFailure),
    onBenchmarkFail: readBool(d.serverPoseOnBenchmarkFail),
    onDetectorBroken: readBool(d.serverPoseOnDetectorBroken),
    onBlindWhileVisible: readBool(d.serverPoseOnBlindWhileVisible),
    onJointUnseen: readBool(d.serverPoseOnJointUnseen),
    rememberLiveSwitch: readBool(d.serverPoseRememberLiveSwitch),
    maxRttMs: readClamped(
      d.serverPoseMaxRttMs,
      RTT_MIN_MS,
      RTT_MAX_MS,
      DEFAULT_POSE_REMOTE_CONFIG.maxRttMs
    ),
  };
}
